import React, { memo, useContext } from 'react'
import CountryFlag from 'react-country-flag'
import PropTypes from 'prop-types'
import { shallowEqual, useSelector } from 'react-redux'

import ConfigContext from '../../contexts/configContext'

import { millisToLocaleString } from '../../helpers/dateHelpers'
import { formatCurrency } from '../../helpers/currencyHelpers'
import { decodeEntities, translate } from '../../helpers/stringHelpers'

// components
import Badge from '../../components/badge'
import Col from '../../components/col'
import List from '../../components/list'
import ListItem from '../../components/listItem'
import Row from '../../components/row'
import ModalHeader from '../modalHeader'
import Modal from '../modal'
import ModalBody from '../modalBody'

const sortBidsByTimestampThenValue = (a, b) => (b.placedAt === a.placedAt)
  ? b.amount - a.amount
  : b.placedAt - a.placedAt

export const BidHistoryModal = ({
  isBidHistoryVisible,
  listingUuid,
  setBidHistoryVisibility
}) => {
  const { locale } = useContext(ConfigContext)
  const listing = useSelector(state => state.lots.listings[listingUuid], shallowEqual)
  const saleStatus = useSelector(state => listingUuid && state.saleStatuses.saleStatuses[listingUuid], shallowEqual)
  const sale = useSelector(state => listingUuid && state.listingSales.sales[listingUuid], shallowEqual)
  const bids = useSelector(state => sale?.bids.map(bidUuid => state.bids.bids[bidUuid]))
  const registrations = useSelector(state => bids?.reduce((acc, val) => {
    if (val) {
      const registration = state.registrations.registrantIdsByUserUuidByAuction[listing?.auctionUuid]?.[val.userUuid]
      acc[val.userUuid] = registration
    }
    return acc
  }, {}), shallowEqual)
  const userUuid = useSelector(state => state.users.user.uuid)
  const isBidHistoryDisabled = useSelector(state => !!state.config.input.options.disableBidHistory)
  if (isBidHistoryDisabled || !listing || listing.isMarketplace || !bids || bids.length === 0) return null
  return (
    <Modal onClose={() => setBidHistoryVisibility(false)} isOpen={isBidHistoryVisible} id='bidHistoryModal'>
      <ModalHeader onClose={() => setBidHistoryVisibility(false)}>
        {translate('JspPublicCodes_JSP_LISTING_BID_HISTORY')}
      </ModalHeader>
      <ModalBody key='bid-history__title'>
        <h2 className='bid-history__title h3'>{listing?.title}</h2>
      </ModalBody>
      <List className='bid-history'>
        {bids.sort(sortBidsByTimestampThenValue).map(bid => {
          const isWinning = saleStatus.highestBidUuid === bid?.uuid && !saleStatus?.highestOfferUuid
          const winningClass = isWinning ? ' bid-history__item--winning' : ''
          const cancelledClass = bid?.cancelled ? ' bid-history__item--cancelled' : ''
          const typeClass = bid?.autobid ? 'autobid' : 'bid'
          const registrant = registrations[bid?.userUuid]
          if (!bid || !registrant) return null

          const formattedValue = formatCurrency(locale, sale.currencyCode, bid.amount)
          const paddleNumber = translate('JspPublicCodes_JSP_REGISTRANT_PADDLE_NO').replace('{0}', registrant.paddleNumber)
          const registrantLocation = `${decodeEntities(registrant.city)}${registrant.county ? `, ${decodeEntities(registrant.county)}` : ''}`
          return (
            <ListItem
              className={`bid-history__item bid-history__item--${typeClass}${winningClass}${cancelledClass}`}
              id={`bid-history__item--${bid.uuid}`}
              key={`bidHistoryModalItem${bid.uuid}`}
            >
              <Row>
                <Col xs={6}>
                  <h5 className='h4 bid__registrant--paddle'>
                    {bid.cancelled
                      ? (
                        <del>{paddleNumber}</del>
                        )
                      : paddleNumber}
                    {registrant.userUuid === userUuid
                      ? (
                        <small className='bid-history__flag--user'>
                          <Badge color='info'>
                            {translate('JspPublicCodes_JSP_LISTING_BID_HISTORY_USER_FLAG')}
                          </Badge>
                        </small>
                        )
                      : null}
                  </h5>
                  <p className='text-muted bid__registrant--location'>
                    {registrant.countryCode
                      ? (
                        <CountryFlag
                          aria-label={registrant.countryCode}
                          countryCode={registrant.countryCode.replace('UK', 'GB')}
                          svg
                          className='lot__country-flag'
                          title={registrant.countryCode}
                        />
                        )
                      : null}
                    {registrantLocation}
                  </p>
                </Col>
                <Col xs={6} className='text-right'>
                  <h4 className='bid__amount'>
                    {bid.cancelled
                      ? (
                        <small className='bid__amount--cancelled'>
                          <Badge>
                            {translate('JspPublicCodes_JSP_BID_CANCELLED')}
                          </Badge>
                        </small>
                        )
                      : null
                    }
                    {isWinning
                      ? (
                        <small className='bid__amount--winning'>
                          <Badge color='success'>
                            {translate(saleStatus.sold ? 'JspPublicCodes_JSP_BID_WON' : 'JspPublicCodes_JSP_BID_WINNING')}
                          </Badge>
                        </small>
                        )
                      : null} {bid.cancelled
                        ? (
                          <del>{formattedValue}</del>
                          )
                        : formattedValue}
                  </h4>
                  <small className='text-muted bid__time'>
                    {bid.autobid ? <strong className='bid__method--autobid'>{translate('JspPublicCodes_JSP_BID_AUTOBID')} - </strong> : ''}
                    {millisToLocaleString(bid.placedAt)}
                  </small>
                </Col>
              </Row>
            </ListItem>
          )
        }
        )}
      </List>
    </Modal>
  )
}

BidHistoryModal.propTypes = {
  isBidHistoryVisible: PropTypes.bool,
  listingUuid: PropTypes.string.isRequired,
  setBidHistoryVisibility: PropTypes.func.isRequired
}

const isEqual = (a, b) => a.isBidHistoryVisible === b.isBidHistoryVisible &&
  a.listingUuid === b.listingUuid

export default memo(BidHistoryModal, isEqual)
